'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { useTheme } from '@birhaus/themes'
import { 
  Search,
  Filter,
  User,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  Edit,
  Trash2,
  Download,
  FileText,
  Lock,
  Unlock,
  Settings,
  Sparkles,
  Calendar,
  MapPin
} from 'lucide-react'

// Local cn utility
const cn = (...inputs: (string | boolean | undefined | null)[]) => twMerge(clsx(inputs))

// V3 Audit Trail variants with glass morphism
const auditTrailV3Variants = cva(
  'group relative transition-all duration-700 ease-smooth overflow-hidden backdrop-blur-md border border-white/30',
  {
    variants: {
      glassVariant: {
        subtle: 'bg-white/40 shadow-lg',
        medium: 'bg-white/60 shadow-xl backdrop-saturate-150',
        intense: 'bg-white/80 shadow-2xl backdrop-saturate-200'
      },
      borderRadius: {
        moderate: 'rounded-2xl',
        generous: 'rounded-3xl',
        radical: 'rounded-[2.5rem]'
      },
      spacing: {
        comfortable: 'p-6',
        generous: 'p-8',
        radical: 'p-12'
      },
      floating: {
        true: 'hover:scale-[1.005] hover:-translate-y-0.5',
        false: ''
      }
    },
    defaultVariants: {
      glassVariant: 'medium',
      borderRadius: 'generous',
      spacing: 'generous',
      floating: false
    }
  }
)

const auditEventVariants = cva(
  'transition-all duration-300 backdrop-blur-sm border border-white/30 rounded-2xl hover:shadow-md',
  {
    variants: {
      severity: {
        info: 'bg-blue-50/60 border-blue-200/50 hover:bg-blue-50/80',
        warning: 'bg-amber-50/60 border-amber-200/50 hover:bg-amber-50/80',
        error: 'bg-red-50/60 border-red-200/50 hover:bg-red-50/80',
        success: 'bg-green-50/60 border-green-200/50 hover:bg-green-50/80',
        critical: 'bg-red-100/70 border-red-300/60 hover:bg-red-100/90'
      }
    },
    defaultVariants: {
      severity: 'info'
    }
  }
)

export interface AuditEventV3 {
  id: string
  timestamp: Date
  userId: string
  userName: string
  userRole: string
  action: string
  actionEs?: string
  actionEn?: string
  resource: string
  resourceType: 'user' | 'document' | 'transaction' | 'system' | 'configuration'
  severity: 'info' | 'warning' | 'error' | 'success' | 'critical'
  description: string
  descriptionEs?: string
  descriptionEn?: string
  metadata?: {
    ipAddress?: string
    userAgent?: string
    location?: string
    sessionId?: string
    entityId?: string
    beforeValue?: any
    afterValue?: any
    requestId?: string
  }
  status: 'logged' | 'reviewed' | 'investigated' | 'resolved'
  reviewedBy?: string
  reviewedAt?: Date
  tags?: string[]
}

export interface AuditFiltersV3 {
  dateRange?: {
    from: Date
    to: Date
  }
  userIds?: string[]
  actions?: string[]
  severities?: ('info' | 'warning' | 'error' | 'success' | 'critical')[]
  resourceTypes?: ('user' | 'document' | 'transaction' | 'system' | 'configuration')[]
  statuses?: ('logged' | 'reviewed' | 'investigated' | 'resolved')[]
  searchTerm?: string
}

export interface BirhausAuditTrailV3Props extends VariantProps<typeof auditTrailV3Variants> {
  // Audit data
  events: AuditEventV3[]
  
  // V3 Glass styling
  enableGlass?: boolean
  glassIntensity?: 'subtle' | 'medium' | 'intense'
  enableFloating?: boolean
  reducedMotion?: boolean
  
  // Display options
  showSearch?: boolean
  showFilters?: boolean
  showUserDetails?: boolean
  showMetadata?: boolean
  showTimeline?: boolean
  itemsPerPage?: number
  
  // Permissions
  canViewDetails?: boolean
  canExportAudit?: boolean
  canReviewEvents?: boolean
  
  // Theme
  themeName?: 'compliance' | 'security' | 'financial' | 'default'
  
  // Interaction
  onEventClick?: (event: AuditEventV3) => void
  onFilterChange?: (filters: AuditFiltersV3) => void
  onExportAudit?: () => void
  onReviewEvent?: (eventId: string) => void
  
  // Standard props
  className?: string
  loading?: boolean
  title?: string
  titleEs?: string
  titleEn?: string
}

/**
 * BirhausAuditTrailV3 - Glass morphism audit trail with comprehensive event tracking
 * 
 * Features:
 * - Glass morphism event cards with severity-based color coding
 * - Real-time audit event filtering and search
 * - Comprehensive metadata display with glass styling
 * - User action tracking with IP and location data
 * - Spanish-first audit terminology and descriptions
 * - Timeline visualization with floating effects
 * - Security-focused event categorization
 * - Export capabilities for compliance reporting
 */
export function BirhausAuditTrailV3({
  events,
  enableGlass = true,
  glassVariant = 'medium',
  borderRadius = 'generous',
  spacing = 'generous',
  floating = false,
  showSearch = true,
  showFilters = true,
  showUserDetails = true,
  showMetadata = false,
  showTimeline = true,
  itemsPerPage = 20,
  canViewDetails = true,
  canExportAudit = false,
  canReviewEvents = false,
  reducedMotion = false,
  themeName = 'compliance',
  onEventClick,
  onFilterChange,
  onExportAudit,
  onReviewEvent,
  className,
  loading = false,
  title,
  titleEs,
  titleEn,
  ...props
}: BirhausAuditTrailV3Props) {
  const { language } = useBirhaus()
  const theme = useTheme(themeName)
  
  const [filters, setFilters] = React.useState<AuditFiltersV3>({})
  const [searchTerm, setSearchTerm] = React.useState('')
  const [showFilterPanel, setShowFilterPanel] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [expandedEvent, setExpandedEvent] = React.useState<string | null>(null)

  // Get action icon
  const getActionIcon = React.useCallback((action: string, resourceType: string) => {
    const actionLower = action.toLowerCase()
    
    if (actionLower.includes('login') || actionLower.includes('auth')) return User
    if (actionLower.includes('create') || actionLower.includes('add')) return FileText
    if (actionLower.includes('update') || actionLower.includes('edit') || actionLower.includes('modify')) return Edit
    if (actionLower.includes('delete') || actionLower.includes('remove')) return Trash2
    if (actionLower.includes('view') || actionLower.includes('access')) return Eye
    if (actionLower.includes('download') || actionLower.includes('export')) return Download
    if (actionLower.includes('lock') || actionLower.includes('block')) return Lock
    if (actionLower.includes('unlock') || actionLower.includes('unblock')) return Unlock
    if (actionLower.includes('config') || actionLower.includes('setting')) return Settings
    
    // Fallback by resource type
    if (resourceType === 'user') return User
    if (resourceType === 'transaction') return FileText
    if (resourceType === 'system') return Settings
    if (resourceType === 'configuration') return Settings
    
    return Shield
  }, [])

  // Get severity info
  const getSeverityInfo = React.useCallback((severity: string) => {
    const severities = {
      info: {
        icon: CheckCircle,
        color: 'text-blue-600',
        label: language === 'es' ? 'Información' : 'Info',
        bgColor: 'bg-blue-100/60'
      },
      warning: {
        icon: AlertTriangle,
        color: 'text-amber-600',
        label: language === 'es' ? 'Advertencia' : 'Warning',
        bgColor: 'bg-amber-100/60'
      },
      error: {
        icon: AlertTriangle,
        color: 'text-red-600',
        label: language === 'es' ? 'Error' : 'Error',
        bgColor: 'bg-red-100/60'
      },
      success: {
        icon: CheckCircle,
        color: 'text-green-600',
        label: language === 'es' ? 'Éxito' : 'Success',
        bgColor: 'bg-green-100/60'
      },
      critical: {
        icon: AlertTriangle,
        color: 'text-red-700',
        label: language === 'es' ? 'Crítico' : 'Critical',
        bgColor: 'bg-red-100/70'
      }
    }
    
    return severities[severity as keyof typeof severities] || severities.info
  }, [language])

  // Filter events
  const filteredEvents = React.useMemo(() => {
    let filtered = events

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply other filters
    if (filters.userIds?.length) {
      filtered = filtered.filter(event => filters.userIds!.includes(event.userId))
    }

    if (filters.severities?.length) {
      filtered = filtered.filter(event => filters.severities!.includes(event.severity))
    }

    if (filters.resourceTypes?.length) {
      filtered = filtered.filter(event => filters.resourceTypes!.includes(event.resourceType))
    }

    if (filters.dateRange) {
      filtered = filtered.filter(event => 
        event.timestamp >= filters.dateRange!.from && 
        event.timestamp <= filters.dateRange!.to
      )
    }

    return filtered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  }, [events, searchTerm, filters])

  // Paginated events
  const paginatedEvents = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filteredEvents.slice(start, end)
  }, [filteredEvents, currentPage, itemsPerPage])

  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage)

  // Handle search
  const handleSearch = React.useCallback((value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }, [])

  // Handle filter change
  const handleFilterChange = React.useCallback((newFilters: AuditFiltersV3) => {
    setFilters(newFilters)
    setCurrentPage(1)
    onFilterChange?.(newFilters)
  }, [onFilterChange])

  // Toggle event details
  const toggleEventDetails = React.useCallback((eventId: string) => {
    setExpandedEvent(prev => prev === eventId ? null : eventId)
  }, [])

  if (loading) {
    return (
      <div className={cn(
        auditTrailV3Variants({
          glassVariant: enableGlass ? glassVariant : undefined,
          borderRadius,
          spacing
        }),
        'animate-pulse',
        !enableGlass && 'bg-white border-gray-200',
        className
      )}>
        <div className="space-y-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200/50 rounded-2xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        auditTrailV3Variants({
          glassVariant: enableGlass ? glassVariant : undefined,
          borderRadius,
          spacing,
          floating: floating && !reducedMotion
        }),
        'space-y-6',
        !enableGlass && 'bg-white border-gray-200',
        className
      )}
      {...props}
    >
      {/* Glass gradient overlay */}
      {enableGlass && (
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
      )}

      {/* Floating sparkle effects */}
      {enableGlass && floating && (
        <div className="absolute top-6 right-6 text-blue-400/40">
          <Sparkles size={20} className="animate-pulse" />
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Shield size={28} className="text-blue-600" />
            {language === 'es' 
              ? (titleEs || title || 'Registro de Auditoría')
              : (titleEn || title || 'Audit Trail')
            }
          </h2>
          <p className="text-gray-600">
            {language === 'es' 
              ? 'Seguimiento completo de eventos del sistema'
              : 'Comprehensive system event tracking'
            }
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Export button */}
          {canExportAudit && (
            <button
              onClick={onExportAudit}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300',
                enableGlass 
                  ? 'bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/40'
                  : 'bg-gray-100 hover:bg-gray-200'
              )}
            >
              <Download size={16} />
              <span className="text-sm font-medium">
                {language === 'es' ? 'Exportar' : 'Export'}
              </span>
            </button>
          )}

          {/* Sparkle decoration */}
          {enableGlass && (
            <div className="text-blue-400/40">
              <Sparkles size={20} className="animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {/* Search and filters */}
      {(showSearch || showFilters) && (
        <div className="relative space-y-4">
          {/* Search */}
          {showSearch && (
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Search size={20} className="text-gray-500" />
              </div>
              <input
                type="text"
                placeholder={
                  language === 'es' 
                    ? 'Buscar eventos, usuarios, acciones...' 
                    : 'Search events, users, actions...'
                }
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={cn(
                  'w-full pl-12 pr-4 py-3 text-base rounded-2xl border-0 outline-0 transition-all duration-300',
                  'focus:ring-2 focus:ring-blue-500/50',
                  enableGlass 
                    ? 'bg-white/70 backdrop-blur-sm placeholder-gray-600 backdrop-saturate-150'
                    : 'bg-gray-50 border border-gray-300 placeholder-gray-500'
                )}
              />
            </div>
          )}

          {/* Filter toggle */}
          {showFilters && (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300',
                  enableGlass 
                    ? 'bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200'
                )}
              >
                <Filter size={16} />
                <span className="text-sm font-medium">
                  {language === 'es' ? 'Filtros' : 'Filters'}
                </span>
              </button>

              {/* Quick severity filters */}
              <div className="flex items-center gap-2">
                {['info', 'warning', 'error', 'critical'].map((severity) => {
                  const severityInfo = getSeverityInfo(severity)
                  const isActive = filters.severities?.includes(severity as any)
                  
                  return (
                    <button
                      key={severity}
                      onClick={() => handleFilterChange({
                        ...filters,
                        severities: isActive 
                          ? filters.severities?.filter(s => s !== severity)
                          : [...(filters.severities || []), severity as any]
                      })}
                      className={cn(
                        'flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-all duration-200',
                        enableGlass ? [
                          isActive
                            ? `${severityInfo.bgColor} backdrop-blur-sm border border-white/40`
                            : 'bg-white/50 hover:bg-white/70 backdrop-blur-sm border border-white/30'
                        ] : [
                          isActive
                            ? severityInfo.bgColor
                            : 'bg-gray-100 hover:bg-gray-200'
                        ]
                      )}
                    >
                      <severityInfo.icon size={12} className={severityInfo.color} />
                      <span className={severityInfo.color}>
                        {severityInfo.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results summary */}
      <div className="text-sm text-gray-600">
        {language === 'es' 
          ? `${filteredEvents.length} eventos encontrados`
          : `${filteredEvents.length} events found`
        }
      </div>

      {/* Audit events list */}
      <div className="relative space-y-3">
        {paginatedEvents.map((event) => {
          const ActionIcon = getActionIcon(event.action, event.resourceType)
          const severityInfo = getSeverityInfo(event.severity)
          const isExpanded = expandedEvent === event.id
          
          return (
            <div
              key={event.id}
              className={cn(
                auditEventVariants({ severity: event.severity }),
                'p-4',
                !enableGlass && 'bg-white border border-gray-200'
              )}
            >
              <div 
                className={cn(
                  'flex items-start gap-4',
                  canViewDetails && 'cursor-pointer'
                )}
                onClick={() => canViewDetails && toggleEventDetails(event.id)}
              >
                {/* Timeline indicator */}
                {showTimeline && (
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      'p-3 rounded-xl transition-colors duration-300',
                      enableGlass 
                        ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                        : 'bg-gray-100'
                    )}>
                      <ActionIcon size={20} className={severityInfo.color} />
                    </div>
                    <div className="w-0.5 h-6 bg-gray-200 mt-2"></div>
                  </div>
                )}

                {/* Event details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-gray-800">
                          {language === 'es' 
                            ? (event.actionEs || event.action)
                            : (event.actionEn || event.action)
                          }
                        </h4>
                        <div className={cn(
                          'inline-flex items-center gap-1 px-2 py-1 rounded text-xs',
                          enableGlass 
                            ? severityInfo.bgColor + ' backdrop-blur-sm'
                            : severityInfo.bgColor
                        )}>
                          <severityInfo.icon size={12} className={severityInfo.color} />
                          {severityInfo.label}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-2">
                        {language === 'es' 
                          ? (event.descriptionEs || event.description)
                          : (event.descriptionEn || event.description)
                        }
                      </p>

                      {showUserDetails && (
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            <span>{event.userName} ({event.userRole})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            <span>
                              {event.timestamp.toLocaleString(
                                language === 'es' ? 'es-PY' : 'en-US'
                              )}
                            </span>
                          </div>
                          {event.metadata?.ipAddress && (
                            <div className="flex items-center gap-1">
                              <MapPin size={12} />
                              <span>{event.metadata.ipAddress}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {canReviewEvents && event.status === 'logged' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onReviewEvent?.(event.id)
                          }}
                          className={cn(
                            'px-3 py-1 rounded-lg text-xs transition-colors',
                            enableGlass 
                              ? 'bg-blue-100/60 text-blue-700 hover:bg-blue-100/80 backdrop-blur-sm'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          )}
                        >
                          {language === 'es' ? 'Revisar' : 'Review'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded metadata */}
                  {isExpanded && showMetadata && event.metadata && (
                    <div className={cn(
                      'mt-4 p-4 rounded-xl transition-colors duration-300',
                      enableGlass 
                        ? 'bg-white/40 backdrop-blur-sm border border-white/30'
                        : 'bg-gray-50'
                    )}>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        {language === 'es' ? 'Metadatos' : 'Metadata'}
                      </h5>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {event.metadata.sessionId && (
                          <div>
                            <span className="text-gray-500">Session ID:</span>
                            <span className="font-mono ml-2">{event.metadata.sessionId}</span>
                          </div>
                        )}
                        {event.metadata.userAgent && (
                          <div className="col-span-2">
                            <span className="text-gray-500">User Agent:</span>
                            <span className="font-mono ml-2 break-all">{event.metadata.userAgent}</span>
                          </div>
                        )}
                        {event.metadata.beforeValue && (
                          <div>
                            <span className="text-gray-500">Before:</span>
                            <pre className="text-xs mt-1 p-2 bg-gray-100/60 rounded">
                              {JSON.stringify(event.metadata.beforeValue, null, 2)}
                            </pre>
                          </div>
                        )}
                        {event.metadata.afterValue && (
                          <div>
                            <span className="text-gray-500">After:</span>
                            <pre className="text-xs mt-1 p-2 bg-gray-100/60 rounded">
                              {JSON.stringify(event.metadata.afterValue, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          {[...Array(Math.min(totalPages, 10))].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={cn(
                'px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                i + 1 === currentPage
                  ? enableGlass
                    ? 'bg-blue-100/80 text-blue-700 backdrop-blur-sm'
                    : 'bg-blue-100 text-blue-700'
                  : enableGlass
                    ? 'bg-white/50 hover:bg-white/70 text-gray-600 backdrop-blur-sm'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              )}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {filteredEvents.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="relative mb-4">
            <Shield size={48} className="text-gray-400 mx-auto" />
            {enableGlass && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-400/20 to-transparent rounded-full blur-xl" />
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {language === 'es' ? 'No se encontraron eventos' : 'No events found'}
          </h3>
          <p className="text-gray-500">
            {language === 'es' 
              ? 'Ajusta los filtros para ver más eventos'
              : 'Adjust filters to see more events'
            }
          </p>
        </div>
      )}
    </div>
  )
}

export type { BirhausAuditTrailV3Props, AuditEventV3, AuditFiltersV3 }